import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";

const COLORS = ["#FACC15", "#22C55E", "#38BDF8", "#F472B6", "#FB923C", "#A78BFA"];

function Confetti({ count, spread }) {
  return Array.from({ length: count }).map((_, i) => (
    <motion.span key={i} className="absolute left-1/2 top-1/3 w-3 h-3 rounded-sm"
      style={{ background: COLORS[i % COLORS.length] }}
      initial={{ opacity: 0, x: 0, y: 0, rotate: 0 }}
      animate={{ opacity: [0, 1, 0], x: Math.cos((i / count) * Math.PI * 2) * spread, y: Math.sin((i / count) * Math.PI * 2) * spread * 0.7 - 60, rotate: 360 }}
      transition={{ delay: 0.45, duration: 1.3, ease: "easeOut" }} />
  ));
}

function StarRain() {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden" data-testid="kiko-star-rain">
      {Array.from({ length: 24 }).map((_, i) => (
        <motion.span key={i} className="absolute text-4xl" style={{ left: `${(i * 37) % 100}%`, color: COLORS[i % COLORS.length] }}
          initial={{ y: "-10vh", rotate: 0 }} animate={{ y: "110vh", rotate: 540 }} transition={{ delay: (i % 8) * 0.12, duration: 2.2, ease: "easeIn" }}>
          ★
        </motion.span>
      ))}
    </div>
  );
}

const DANCE = {
  1: { y: [0, -28, 0, -18, 0], rotate: [0, -8, 8, -6, 0], scale: 1 },
  3: { y: [0, -60, 0, -40, 0], rotate: [0, -12, 12, -8, 0], scale: [1, 1.15, 1] },
  5: { y: [0, -50, 0, -50, 0, -30, 0], rotate: [0, -20, 20, -20, 20, 0], scale: [1, 1.2, 1] },
  10: { y: [0, -80, 0, -60, 0, -40, 0], rotate: [0, 360, 360], scale: [1, 1.3, 1.1, 1.3, 1] },
};

// Grades 1-3: Kiko the Mauritian gecko. cheer = { n, msg, streak?, kind: "win" | "try" }.
// Wins: runs in, dances (stronger at 3, 5 and 10 in a row). Mistakes: walks in gently to encourage.
let lastCheer = 0;

export default function KikoCheer({ cheer }) {
  const [shown, setShown] = useState(null);
  useEffect(() => {
    if (!cheer || cheer.n === lastCheer) return undefined;
    lastCheer = cheer.n;
    setShown(cheer);
    const t = setTimeout(() => setShown(null), cheer.kind === "try" ? 2800 : cheer.streak >= 10 ? 3400 : 2600);
    return () => clearTimeout(t);
  }, [cheer]);

  const tier = shown?.kind === "try" ? 0 : shown?.streak >= 10 ? 10 : shown?.streak >= 5 ? 5 : shown?.streak >= 3 ? 3 : 1;
  return createPortal(
    <AnimatePresence>
      {shown && (
        <motion.div key={shown.n} className={`fixed inset-x-0 bottom-0 z-50 pointer-events-none flex ${tier ? "justify-center" : "justify-start pl-2"}`} data-testid="kiko-cheer" data-tier={tier}
          initial={{ x: "-110vw" }} animate={{ x: 0 }} exit={{ x: "110vw" }}
          transition={tier ? { type: "spring", stiffness: 120, damping: 16 } : { duration: 0.9, ease: "easeOut" }}>
          {tier === 10 && <StarRain />}
          <div className="relative flex items-end gap-2 pb-4">
            {tier > 0 && <Confetti count={tier >= 5 ? 28 : 14} spread={tier >= 5 ? 230 : 160} />}
            <motion.img src="/mascot/kiko.png" alt="Kiko" draggable={false} data-testid="kiko-img"
              className={`${tier >= 3 ? "w-40 sm:w-48" : tier ? "w-32 sm:w-40" : "w-20 sm:w-24"} drop-shadow-xl select-none`}
              animate={tier ? DANCE[tier] : { rotate: [0, -6, 0, -6, 0], y: [0, -6, 0] }}
              transition={{ delay: 0.4, duration: tier >= 5 ? 1.8 : 1.3, ease: "easeInOut" }} />
            <motion.div data-testid="kiko-bubble"
              className={`${tier ? "mb-24 sm:mb-28" : "mb-14 sm:mb-16"} rounded-2xl border-4 border-slate-900 px-4 py-2 font-kids font-bold text-slate-900 shadow-[4px_4px_0_0_#0F172A] ${tier ? "bg-white text-xl sm:text-3xl max-w-[58vw] sm:max-w-md" : "bg-sky-100 text-base sm:text-lg max-w-[13rem]"}`}
              initial={{ scale: 0, rotate: -10 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.35, type: "spring", stiffness: 260, damping: 14 }}>
              {shown.msg}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
